import { ComponentInstance, computed, defineComponent, nextTick, ref, Ref, useModel, watch } from "vue";
import { convertTo12Hour, now } from "../../skyward.base.time";
import { addScopedGlobalListener, DefaultVueProp, VueProp } from "../core/vueHelpers";
import Calendar, { CalendarExposed } from "../datePicker/components/Calendar";
import FooterToolbar from "../datePicker/components/FooterToolbar";
import MonthSelector from "../datePicker/components/MonthSelector";
import TimeSelector, { TimeSelectorExposed } from "../timePicker/components/TimeSelector";
import { DayClasses } from "../datePicker/components/Day";

const DateTimePickerModalClasses =
{
    modal: "dateTimePickerModal",
    modalSecondary: "dateTimePickerModal--dateTime"
};

const DEFAULT_YEAR_LOW = 1900;

export default /*#__PURE__*/ defineComponent(
{
    name: "datePickerModal",
    emits:
    {
        "update:value": (_: Date) => true,
    },
    props: 
    {
        close: VueProp<() => void>(Function),
        inputRef: VueProp<Ref<HTMLInputElement | undefined, HTMLInputElement | undefined>>(Object),
        value: DefaultVueProp<Date>(Date, { default: () => now() }),
    },
    setup(props, context)
    {
        const value = useModel(props, "value");
        const tempValue = ref<Date>(new Date(value.value.getTime()));
        const today = now();
        const currentMonth = props.value ? props.value.getMonth() + 1 : today.getMonth() + 1;
        const currentYear = props.value ? props.value.getFullYear() : today.getFullYear();
        const month = ref<number>(currentMonth);
        const year = ref<number>(currentYear);
        const yearHigh = computed(() => props.value && props.value.getFullYear() > today.getFullYear() + 10 ? props.value.getFullYear() : today.getFullYear() + 10);
        const yearLow = computed(() => props.value && props.value.getFullYear() < DEFAULT_YEAR_LOW ? props.value.getFullYear() : DEFAULT_YEAR_LOW);

        const currentHour = props.value ? convertTo12Hour(props.value.getHours()) : 12;
        const currentMinute = props.value ? props.value.getMinutes() : 0;
        const currentAmPm = props.value ? (props.value.getHours() >= 12 ? "PM" : "AM") : "AM";

        const hour = ref<number | undefined>(currentHour);
        const minute = ref<number | undefined>(currentMinute);
        const amPm = ref<"AM" | "PM">(currentAmPm);
        const modal = ref<HTMLDivElement>();

        watch(() => value.value, (newValue) =>
        {
            tempValue.value = newValue;
            setHour(newValue);
            minute.value = newValue.getMinutes();
        });

        watch(() => hour.value, (newHour) =>
        {
            let hourToSet = newHour;
            if (hourToSet === undefined || isNaN(newHour!))
            {
                hourToSet = 0;
            }
            if (amPm.value === "PM")
            {
                hourToSet += 12;
            }
            tempValue.value.setHours(hourToSet);
        });

        watch(() => minute.value, (newMinute) =>
        {
            let minuteToSet = newMinute;
            if (minuteToSet === undefined || isNaN(newMinute!))
            {
                minuteToSet = 0;
            }
            tempValue.value.setMinutes(minuteToSet ?? 0);
        });

        watch(() => amPm.value, (newAmPm) =>
        {
            if (newAmPm === "AM")
            {
                tempValue.value.setHours(tempValue.value.getHours() - 12);
            }
            else
            {
                tempValue.value.setHours(tempValue.value.getHours() + 12);
            }
        });

        addScopedGlobalListener(document.body, "keydown", handleKeydown);
        const calendarRef = ref<ComponentInstance<typeof Calendar> & CalendarExposed>();
        const timeSelectorRef = ref<ComponentInstance<typeof TimeSelector> & TimeSelectorExposed>();

        context.expose(
        {
            focusCurrentDay() { calendarRef.value?.focusCurrentDay(); },
            focusHourInput() { timeSelectorRef.value?.focusHourInput(); },
            focusMinuteInput() { timeSelectorRef.value?.focusMinuteInput(); },
            focusAmPmToggle() { timeSelectorRef.value?.focusAmPmToggle(); }
        });

        return () => (
            <div class={[DateTimePickerModalClasses.modal, DateTimePickerModalClasses.modalSecondary]} ref={modal}>
                <MonthSelector
                    month={month.value}
                    year={year.value}
                    yearLow={yearLow.value}
                    yearHigh={yearHigh.value}
                    onUpdate:month={value => (month.value = value)}
                    onUpdate:year={value => (year.value = value)}
                />
                <Calendar
                    month={month.value}
                    year={year.value}
                    yearLow={yearLow.value}
                    yearHigh={yearHigh.value}
                    selectedDate={tempValue.value}
                    onUpdate:month={newMonth => month.value = newMonth}
                    onUpdate:year={newYear => year.value = newYear}
                    onUpdate:selectedDate={updateSelectedDate}
                    ref={calendarRef}
                />
                <TimeSelector
                    ref={timeSelectorRef}
                    hour={hour.value}
                    minute={minute.value}
                    amPm={amPm.value}
                    onUpdate:hour={value => (hour.value = value)}
                    onUpdate:minute={value => (minute.value = value)}
                    onUpdate:amPm={value => (amPm.value = value)}
                />
                <FooterToolbar buttonText="Now" onButtonClick={onNowClick} onDoneClick={updateAndClose} />
            </div>
        );

        function updateSelectedDate(date: Date)
        {
            tempValue.value = date;
            timeSelectorRef.value?.focusHourInput();
        }

        async function handleKeydown(event: JQuery.KeyDownEvent)
        {
            const key = event.key.toUpperCase();
            const target = event.target as HTMLElement;
            // Prevent hot keys in month and year search boxes
            if (target.classList.contains("quickEELSelector__button"))
            {
                if (key !== "TAB")
                {
                    event.preventDefault();
                }
                return;
            }

            if (key === "ARROWUP" || key === "ARROWDOWN" || key === "PAGEUP" || key === "PAGEDOWN" || key === "HOME" || key === "END")
            {
                event.preventDefault();
            }

            if (event.altKey && (key === "ARROWUP" || key === "ARROWDOWN"))
            {
                props.inputRef.value?.focus();
                return;
            }

            if (key === "ENTER" && target.classList.contains(DayClasses.day))
            {
                timeSelectorRef.value?.focusHourInput();
                return;
            }

            // Handle shortcut keys (T = Today, R = Tomorrow, Y = Yesterday, C = Current, N = Now)
            const validShortcutKeys = ["T", "R", "Y", "C", "N"];
            if (validShortcutKeys.includes(key))
            {
                const shortcutDate = now();
                let closePopup = true;

                switch (key)
                {
                    case "R":
                        shortcutDate.setDate(shortcutDate.getDate() + 1);
                        timeSelectorRef.value?.focusHourInput();
                        closePopup = false;
                        break;
                    case "Y":
                        shortcutDate.setDate(shortcutDate.getDate() - 1);
                        timeSelectorRef.value?.focusHourInput();
                        closePopup = false;
                        break;
                    case "T":
                        shortcutDate.setHours(value.value.getHours());
                        shortcutDate.setMinutes(value.value.getMinutes());
                        shortcutDate.setSeconds(value.value.getSeconds());
                        break;
                }

                tempValue.value = shortcutDate;
                setHour(shortcutDate);
                minute.value = shortcutDate.getMinutes();
                month.value = shortcutDate.getMonth() + 1;
                year.value = shortcutDate.getFullYear();

                if (closePopup)
                {
                    await nextTick();
                    updateAndClose();
                }
            }
        }

        async function onNowClick()
        {
            const today = now();

            month.value = today.getMonth() + 1;
            year.value = today.getFullYear();

            const currentHour = today.getHours();
            hour.value = convertTo12Hour(currentHour);
            if (currentHour >= 12)
            {
                amPm.value = "PM";
            }
            else
            {
                amPm.value = "AM";
            }

            minute.value = today.getMinutes();
            tempValue.value = today;

            await nextTick();
            updateAndClose();
        }

        function updateAndClose()
        {
            if (value.value.getTime() === tempValue.value.getTime())
            {
                context.emit("update:value", tempValue.value);
            }
            else
            {
                value.value = tempValue.value;
            }

            props.close();
        }

        function setHour(date: Date)
        {
            let newHour = date.getHours();
            if (newHour >= 12)
            {
                amPm.value = "PM";
            }
            else
            {
                amPm.value = "AM"
            }

            if (newHour === 0)
            {
                newHour = 12;
            }
            else if (newHour > 12)
            {
                newHour %= 12;
            }

            hour.value = newHour;
        }
    }
});
